import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, useDataGridColumnsState } from '@ibsys/kit';
import type { DataGridLeafColumn } from '@ibsys/kit';
import { CritStatItem } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { criterionKey } from '../model/hiddenCriteria';
import { critCellClass, dashboardGridHeight, deltaClass, deltaText, fmt } from './utils';

type Props = {
  items: CritStatItem[];
  /** Пользовательские пороги зон, по которым окрашиваются ячейки баллов. */
  zoneRanges: QualityZoneRanges;
};

/** Таблица отклонений по критериям мини-отчёта: балл консультанта, команды и их разница. */
export const CritStatsTable: FC<Props> = ({ items, zoneRanges }) => {
  const { t } = useTranslation('qualityDashboard');

  const columns = useMemo((): DataGridLeafColumn<CritStatItem>[] => [
    {
      id: 'criterion',
      header: t('consultantReport.columns.criterion'),
      accessor: 'name',
      size: 220,
    },
    {
      id: 'consultant',
      header: t('consultantReport.columns.consultant'),
      accessor: 'consultantAvg',
      size: 110,
      align: 'center',
      className: 'font-bold',
      cellClassName: ({ row }) => critCellClass(row.consultantAvg, row.maxScore, zoneRanges),
      cell: ({ value }) => fmt(value as number | null),
    },
    {
      id: 'team',
      header: t('consultantReport.columns.team'),
      accessor: 'teamAvg',
      size: 110,
      align: 'center',
      className: 'font-bold',
      cellClassName: ({ row }) => critCellClass(row.teamAvg, row.maxScore, zoneRanges),
      cell: ({ value }) => fmt(value as number | null),
    },
    {
      id: 'deviation',
      header: t('consultantReport.columns.deviation'),
      accessor: 'diff',
      size: 110,
      align: 'center',
      cellClassName: ({ value }) => deltaClass(value as number | null),
      cell: ({ value }) => deltaText(value as number | null),
    },
  ], [t, zoneRanges]);

  const { gridProps, panelProps } = useDataGridColumnsState({
    defaultOrder: ['criterion', 'consultant', 'team', 'deviation'],
    defaultPinning: { left: ['criterion'], right: [] },
  });

  const tableHeight = dashboardGridHeight(items.length);

  // Строку опознаёт пара «заключение + имя»: одноимённые критерии разных заключений — разные строки.
  return (
    <DataGrid
      {...gridProps}
      columns={columns}
      dataSource={{ mode: 'client', data: items }}
      getRowId={(r) => criterionKey(r.expertReportId, r.name)}
      enableColumnResizing
      enableColumnReordering
      enableColumnPinning
      columnsPanel={{ showPinning: true, onReset: panelProps.onReset }}
      height={tableHeight}
    />
  );
};
